import React, { useEffect, useMemo } from "react";
import Grid from '@mui/material/Grid';
import OutlinedInput from '@mui/material/OutlinedInput';
import { Select, MenuItem, Paper, Button, IconButton, Chip } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import SpeedDial from '@mui/material/SpeedDial';
import { useUsersManagementController } from "./context/users-management-controller";
import { useUsersManagementState } from "./context/users-management-context";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DefaultDrawer from "../components/default-drawer";
import UsersManagementForm from "./components/users-management-form";
import { getDataGridLocaleText, dataGridDefaultSx, dataGridDefaultProps } from "../../config/datagrid-config";
import PasswordResetDialog from "../components/password-reset-dialog";

export const UsersManagement = () => {
    const {
        handleFilterChange,
        clearFilters,
        loadData,
        onChangePagination,
        searchUsers,
        openDrawer,
        closeDrawer,
        saveUser,
        toggleUserStatus,
        resetUserPassword,
        closePasswordResetDialog,
        loadLookupData
    } = useUsersManagementController();

    const {
        usersPagedRequest,
        usersPagedResponse,
        isLoadingData,
        isDrawerOpen,
        isEditing,
        passwordResetDialog
    } = useUsersManagementState();

    useEffect(() => {
        (async () => {
            await Promise.all([
                loadData(usersPagedRequest),
                loadLookupData()
            ]);
        })();
    }, []);

    const paginationModel = useMemo(() => ({
        page: usersPagedRequest?.page ?? 0,
        pageSize: usersPagedRequest?.pageSize ?? 50
    }), [usersPagedRequest?.page, usersPagedRequest?.pageSize]);

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: 'Nome',
            flex: 1,
            minWidth: 200,
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 1,
            minWidth: 200,
        },
        {
            field: 'documentNumber',
            headerName: 'CPF',
            width: 150,
        },
        {
            field: 'accessGroupName',
            headerName: 'Grupo de Acesso',
            width: 150,
        },
        {
            field: 'isActive',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => (
                <Chip
                    label={params.value ? 'Ativo' : 'Inativo'}
                    color={params.value ? 'success' : 'error'}
                    size="small"
                />
            ),
        },
        {
            field: 'actions',
            headerName: 'Ações',
            width: 140,
            sortable: false,
            renderCell: (params) => (
                <React.Fragment>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openDrawer(true, params.row.userId)}
                        title="Editar"
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        color={params.row.isActive ? 'warning' : 'success'}
                        onClick={() => toggleUserStatus(params.row.userId, params.row.isActive)}
                        title={params.row.isActive ? 'Desativar' : 'Ativar'}
                    >
                        {params.row.isActive ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />}
                    </IconButton>
                    <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => resetUserPassword(params.row.userId, params.row.name, params.row.email)}
                        title="Redefinir Senha"
                    >
                        <VpnKeyIcon fontSize="small" />
                    </IconButton>
                </React.Fragment>
            ),
        },
    ];

    return (
        <React.Fragment>
            <Grid item xs={12} md={12} lg={12}>
                <Paper
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <InputLabel htmlFor="input-name">Nome</InputLabel>
                            <OutlinedInput
                                id="input-name"
                                name="input-name"
                                value={usersPagedRequest?.name || ''}
                                onChange={handleFilterChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <InputLabel htmlFor="input-email">Email</InputLabel>
                            <OutlinedInput
                                id="input-email"
                                name="input-email"
                                value={usersPagedRequest?.email || ''}
                                onChange={handleFilterChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <InputLabel htmlFor="select-status">Status</InputLabel>
                            <Select
                                id="select-status"
                                name="select-status"
                                value={usersPagedRequest?.isActive === undefined ? "" : String(usersPagedRequest.isActive)}
                                onChange={handleFilterChange}
                                fullWidth
                                size="small"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="true">Ativos</MenuItem>
                                <MenuItem value="false">Inativos</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<SearchIcon />}
                                    fullWidth
                                    onClick={searchUsers}
                                    size="small"
                                >
                                    Buscar
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<ClearIcon />}
                                    fullWidth
                                    onClick={clearFilters}
                                    size="small"
                                >
                                    Limpar
                                </Button>
                            </div>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
                <Paper style={{ height: 600, width: '100%' }}>
                    <DataGrid
                        rows={usersPagedResponse?.items || []}
                        columns={columns}
                        getRowId={(row) => row.userId}
                        {...dataGridDefaultProps}
                        onPaginationModelChange={(e) => {
                            onChangePagination(e.page, e.pageSize);
                        }}
                        sx={dataGridDefaultSx}
                        localeText={getDataGridLocaleText(
                            usersPagedResponse?.totalRecords || 0,
                            usersPagedResponse?.pageSize || 10
                        )}
                        paginationModel={paginationModel}
                        rowCount={usersPagedResponse?.totalRecords || 0}
                        loading={isLoadingData}
                    />
                </Paper>

                <SpeedDial
                    ariaLabel="Adicionar Usuário"
                    sx={{ position: 'fixed', bottom: 16, right: 16 }}
                    icon={<AddIcon />}
                    onClick={() => openDrawer(false)}
                />

                <DefaultDrawer
                    isOpen={isDrawerOpen}
                    onClose={closeDrawer}
                    title={isEditing ? "Editar Usuário" : "Novo Usuário"}
                    actions={() => {
                        return (
                            <>
                                <Button
                                    variant="contained"
                                    onClick={saveUser}
                                    size="large"
                                >
                                    Salvar
                                </Button>
                            </>
                        );
                    }}>
                    <UsersManagementForm />
                </DefaultDrawer>

                <PasswordResetDialog
                    open={passwordResetDialog.open}
                    userName={passwordResetDialog.userName}
                    userEmail={passwordResetDialog.userEmail}
                    newPassword={passwordResetDialog.newPassword}
                    onClose={closePasswordResetDialog}
                />
            </Grid>
        </React.Fragment>
    );
};

export default UsersManagement;
